import { LitElement, html, css, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";
import "@bee/pattern-library";
import type { UserInfo } from "../types";
import { routes, type RouteConfig } from "../router";

@customElement("dashboard-sidebar")
export class DashboardSidebar extends LitElement {
  static styles = css`
    :host {
      display: block;
      position: fixed;
      left: 0;
      top: 0;
      bottom: 0;
      width: var(--sizes-72, 72px);
      background-color: var(--colors-background-bg-gray-50, #f9fafb);
      border-right: 1px solid var(--colors-border-border-gray-300, #d0d5dd);
      z-index: 100;
      font-family: Geist, sans-serif;
    }

    nav {
      display: flex;
      flex-direction: column;
      height: 100%;
    }

    .header {
      display: flex;
      align-items: center;
      justify-content: center;
      height: var(--sizes-88, 88px);
      border-bottom: 1px solid var(--colors-border-border-gray-300, #d0d5dd);
      padding: 0;
    }

    .logo {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      background-color: var(--colors-brand-accuris-500, #1000e8);
      border-radius: var(--radius-sm, 6px);
    }

    .logo svg {
      width: 20px;
      height: 20px;
    }

    .body {
      flex: 1;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: var(--spacing-4, 4px);
      padding: var(--spacing-12, 12px) 0;

      /* Override bee icon tokens for light sidebar context */
      --colors-components-icons-fg-default-gray-500-alt: var(--colors-text-gray-700, #344054);
      --colors-components-icons-fg-hover-gray-600: var(--colors-background-bg-brand-600, #1153d6);
      --colors-components-icons-bg-hover-gray-200-alt: var(--colors-background-bg-brand-50, #eff4ff);
    }

    .nav-item {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      border-radius: var(--radius-sm, 6px);
      cursor: pointer;
      transition: background-color 0.15s ease;
    }

    .nav-item.active {
      background-color: var(--colors-background-bg-brand-50, #eff4ff);
    }

    .nav-item.active bee-icon-button {
      --colors-components-icons-fg-default-gray-500-alt: var(--colors-background-bg-brand-600, #1153d6);
    }

    .footer {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: var(--spacing-12, 12px);
      padding: var(--spacing-16, 16px) 0;
      border-top: 1px solid var(--colors-border-border-gray-300, #d0d5dd);

      /* Override bee icon tokens for light sidebar context */
      --colors-components-icons-fg-default-gray-500-alt: var(--colors-text-gray-700, #344054);
      --colors-components-icons-fg-hover-gray-600: var(--colors-background-bg-brand-600, #1153d6);
      --colors-components-icons-bg-hover-gray-200-alt: var(--colors-background-bg-brand-50, #eff4ff);
    }

    .avatar {
      width: 32px;
      height: 32px;
      border-radius: var(--radius-full, 50%);
      border: 2px solid var(--colors-border-border-gray-300, #d0d5dd);
    }
  `;

  @property({ type: Object }) user: UserInfo | null = null;
  @property({ type: String }) currentPath = "/";

  private _handleLogout() {
    this.dispatchEvent(new CustomEvent("logout", { bubbles: true, composed: true }));
  }

  private _handleNav(route: RouteConfig) {
    this.dispatchEvent(new CustomEvent("navigate", { detail: route.path, bubbles: true, composed: true }));
  }

  private _isActive(route: RouteConfig): boolean {
    if (route.path === "/") return this.currentPath === "/";
    return this.currentPath.startsWith(route.path);
  }

  private _getNavTooltipAnchor(route: RouteConfig): string {
    return `nav-${route.path === "/" ? "dashboard" : route.path.slice(1)}`;
  }

  render() {
    return html`
      <nav>
        <div class="header">
          <div class="logo" title="Accuris">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M17.104 12.9361L16.6468 11.8521L15.4606 8.93755L14.5527 6.73367L11.8489 0.136396L11.7959 0H8.09145L5.36117 6.73367L2.86282 12.9361L2.05434 14.9103V14.9246L0.36448 19.0955V19.1098L0.125911 19.6985L0 20H3.86349L5.03645 17.028L13.4526 13.2807L16.057 20H20L17.1107 12.9361H17.104ZM9.98012 4.40775L10.888 6.73367L12.2929 10.3589L6.67329 12.8643V12.85L9.07223 6.73367L9.98012 4.40775Z"
                fill="white"
              />
            </svg>
          </div>
        </div>
        <div class="body">
          ${routes.map((route) => html`
            <div
              id=${this._getNavTooltipAnchor(route)}
              class="nav-item ${this._isActive(route) ? "active" : ""}"
              @click=${() => this._handleNav(route)}
            >
              <bee-icon-button
                bee-type=${this._isActive(route) ? "secondary" : "tertiary"}
                icon=${route.icon}
                size="large"
                aria-label=${route.label}
              ></bee-icon-button>
            </div>
            <bee-tooltip
              anchor=${this._getNavTooltipAnchor(route)}
              content=${route.label}
              placement="right"
              align="left"
              delay="150"
              hoist
            ></bee-tooltip>
          `)}
        </div>
        <div class="footer">
          ${this.user ? html`
            <img
              class="avatar"
              src="${this.user.avatarUrl}"
              alt="${this.user.login}"
              title="${this.user.login}"
            />
          ` : nothing}
          <bee-interactive-icon
            icon="log-out"
            color="gray"
            shape="rounded-square"
            size="medium"
            title="Logout"
            @click=${this._handleLogout}
          ></bee-interactive-icon>
        </div>
      </nav>
    `;
  }
}
